import React from 'react';
import { AvButton, AvSnackBarMessage } from '@fabric-ui/components';
import { CheckFull, Info, WarningFull, X } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, BoxProps, Slide } from '@mui/material';
import { CustomContentProps, SnackbarProvider } from 'notistack';

const iconVariants = {
  success: <CheckFull />,
  error: <WarningFull />,
  warning: <WarningFull />,
  info: <Info />,
  default: <Info />,
};

const variantColors = {
  success: {
    background: 'var(--color-surface-status-success-primary)',
    icon: 'var(--color-content-status-success-secondary)',
  },
  error: {
    background: 'var(--color-surface-status-danger-primary)',
    icon: 'var(--color-content-status-danger-secondary)',
  },
  info: {
    background: 'var(--color-surface-status-info-primary)',
    icon: 'var(--color-content-status-info-secondary)',
  },
  warning: {
    background: 'var(--color-surface-status-warning-primary)',
    icon: 'var(--color-content-status-warning-secondary)',
  },
  default: {
    background: 'var(--color-surface-status-info-primary)',
    icon: 'var(--color-content-status-info-secondary)',
  },
};

type NotificationAlertVariant = keyof typeof variantColors;

type NotificationAlertProps = {
  variant?: NotificationAlertVariant;
  message: React.ReactNode;
  action?: React.ReactNode;
} & BoxProps;

export const NotificationAlert = ({ variant = 'default', message, action, ref, ...props }: NotificationAlertProps) => {
  const colors = variantColors[variant];
  const icon = iconVariants[variant];

  return (
    <Box
      ref={ref}
      role="alert"
      {...props}
      sx={{
        ...flex.justifyBetweenStart,
        backgroundColor: colors.background,
        color: 'var(--color-content-base-primary)',
        minWidth: 390,
        maxWidth: 600,
        minHeight: 44,
        padding: '11px 16px',
        borderRadius: 3,
        boxShadow: '0px 0px 4px 0px var(--color-effects-color-default), 0px 2px 4px 0px var(--color-effects-color-strong)',
        svg: { width: 20, height: 20 },
        ...props.sx,
      }}>
      <Box sx={{ ...flex.itemsStart, gap: 1, flex: 1 }}>
        {icon && <Box sx={{ color: colors.icon, flexShrink: 0, pt: '1px' }}>{icon}</Box>}
        <Box sx={{ flex: 1, wordBreak: 'break-word' }}>{message}</Box>
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
  );
};

const CustomSnackbar = React.forwardRef<HTMLDivElement, CustomContentProps>(({ action, id, message, variant = 'default' }, ref) => {
  const renderedAction = typeof action === 'function' ? action(id) : action;
  return <NotificationAlert ref={ref} variant={variant as NotificationAlertVariant} message={message} action={renderedAction} />;
});

CustomSnackbar.displayName = 'CustomSnackbar';

const StaticErrorSnackbar = React.forwardRef<HTMLDivElement, CustomContentProps & { data?: any }>(({ id, data }, ref) => (
  <AvSnackBarMessage ref={ref} id={id} {...data} />
));

StaticErrorSnackbar.displayName = 'StaticErrorSnackbar';

export default function AvNotificationProvider({ children }: React.PropsWithChildren = {}): React.ReactNode {
  const notificationRef = React.createRef<SnackbarProvider>();

  const dismiss = key => (
    <AvButton
      sx={{ color: 'var(--color-content-base-tertiary)', ':hover': { color: 'var(--color-content-base-primary)' } }}
      aria-label="close"
      onClick={e => {
        e.stopPropagation();
        notificationRef.current?.closeSnackbar(key);
      }}>
      <X />
    </AvButton>
  );

  return (
    <SnackbarProvider
      maxSnack={2}
      preventDuplicate
      domRoot={document.body}
      ref={notificationRef}
      iconVariant={iconVariants}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      action={dismiss}
      TransitionComponent={Slide}
      Components={{
        default: CustomSnackbar,
        success: CustomSnackbar,
        error: CustomSnackbar,
        warning: CustomSnackbar,
        info: CustomSnackbar,
        static: StaticErrorSnackbar,
      }}>
      {children}
    </SnackbarProvider>
  );
}
